import React, {useEffect, useState} from "react";
import {Typography} from "@mui/material";
import {useNavigate} from "react-router";
import {CheckCircleOutlined, ErrorOutlineOutlined, ScheduleOutlined} from "@mui/icons-material";

import {TrainingDue, getMyTraining} from "../../data/training";
import {useSessionContext} from "../../util/SessionContext";
import {Blue3, Grey5, Grey7, Grey17, White} from "../../theme/styles";

// What the signed-in person owes, on the page they land on.
//
// THE HOME PAGE AND "MY TRAINING" ARE NOT THE SAME SCREEN, deliberately. This one answers "what needs
// me right now": capped, most urgent first, and it exists so that nobody has to remember to go and
// look -- training nobody remembers to check is training that does not happen, and in a regulated shop
// that is a finding. My Training is the working surface: the whole list, and the place the quiz is
// actually taken. This panel never takes a quiz; every button on it leads there.
//
// IT DERIVES FROM ONE READ. The counters and the rows are the same payload counted two ways, so they
// can never disagree with each other the way two independent fetches would.

const SHOWN = 5;

function Counter({icon, tint, count, label}: {icon: React.ReactNode; tint: string; count: number; label: string}) {
    return (
        <div style={{flex: 1, minWidth: 220, display: "flex", alignItems: "center", gap: 16,
                     padding: "18px 22px", backgroundColor: White, border: "1px solid #EAECF0",
                     borderRadius: 12, boxSizing: "border-box"}}>
            <div style={{width: 40, height: 40, borderRadius: 10, backgroundColor: tint,
                         display: "flex", alignItems: "center", justifyContent: "center"}}>
                {icon}
            </div>
            <div>
                <Typography style={{fontSize: 28, fontWeight: 700, color: Grey17, lineHeight: 1.1}}>{count}</Typography>
                <Typography style={{fontSize: 13, color: Grey7}}>{label}</Typography>
            </div>
        </div>
    );
}

export default function TrainingAttention() {
    const sessionContext = useSessionContext();
    const navigate = useNavigate();
    const [due, setDue] = useState<TrainingDue[] | null>(null);

    useEffect(() => {
        // QUIET ON FAILURE. A landing page that shows an error box because one panel could not load is
        // worse than a landing page missing a panel: the rest of the page is fine and the person is
        // trying to get somewhere else. My Training says what went wrong, because there it matters.
        getMyTraining(sessionContext)
            // COALESCED, because "quiet on failure" has to mean an unexpected PAYLOAD too, not only
            // a rejected promise. A response without Training left `due` undefined, which slipped
            // past the `due === null` guard below and threw on .filter -- taking the whole landing
            // page down with it, which is the exact outcome the comment above forbids.
            .then(result => setDue(result?.Training ?? []))
            .catch(() => setDue([]));
    }, [sessionContext]);

    if (due === null) return null;

    const pending = due.filter(one => one.State !== "COMPLETE");
    const completed = due.length - pending.length;
    if (due.length === 0) return null;      // no curriculum names this person; nothing to say

    return (
        <div style={{marginTop: 28, maxWidth: 1000}}>
            <div style={{display: "flex", gap: 16, flexWrap: "wrap"}}>
                {/* THE LABEL NAMES THE SUBJECT, because these tiles stand alone. "Pending" and
                    "Completed" said nothing about WHAT -- and when nothing is pending the card
                    below that would have explained them does not render at all, so the whole panel
                    was two bare numbers under "What needs you". Carmen Solis's dashboard read
                    "0 Pending / 1 Completed" with no indication either concerned training;
                    reported by Ed. ApprovalAttention had this right already: its counter reads
                    "waiting on your signature", naming the act rather than the object. */}
                <Counter icon={<ScheduleOutlined sx={{color: "#B54708", fontSize: 22}}/>} tint="#FFFAEB"
                         count={pending.length} label="training pending"/>
                <Counter icon={<CheckCircleOutlined sx={{color: "#067647", fontSize: 22}}/>} tint="#ECFDF3"
                         count={completed} label="training completed"/>
            </div>

            {pending.length > 0 ? (
                <div style={{marginTop: 16, display: "flex", gap: 16, padding: "20px 22px",
                             backgroundColor: White, border: "1px solid #EAECF0", borderRadius: 12}}>
                    <ErrorOutlineOutlined sx={{color: "#B42318", fontSize: 22, marginTop: "2px"}}/>
                    <div style={{flex: 1, minWidth: 0}}>
                        <Typography style={{fontSize: 15, color: Grey17, marginBottom: 14}}>
                            You have training requiring your attention
                        </Typography>

                        {pending.slice(0, SHOWN).map(row => {
                            const overdue = row.State === "OVERDUE";
                            return (
                                <div key={row.ObjectRevisionUSID}
                                     style={{display: "flex", alignItems: "center", gap: 12, marginBottom: 10,
                                             padding: "12px 14px", borderRadius: 8, boxSizing: "border-box",
                                             backgroundColor: overdue ? "#FFFBFA" : "#FFFCF5",
                                             border: `1px solid ${overdue ? "#FDA29B" : "#FEDF89"}`}}>
                                    <div style={{flex: 1, minWidth: 0}}>
                                        {/* THE TRAINING IS THE HEADLINE, the document is what it is about --
                                            a trainee is doing "Chemical Safety Training", the SDS is just
                                            what they read. The document's ID stays visible underneath,
                                            because in a regulated shop that number is how people refer to
                                            the thing they were trained on. */}
                                        <Typography style={{fontSize: 14, fontWeight: 600, color: Grey17,
                                                            overflow: "hidden", textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"}}>
                                            {row.CurriculumName || row.Name}
                                        </Typography>
                                        <Typography style={{fontSize: 12, color: Grey5, marginTop: 2,
                                                            overflow: "hidden", textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"}}>
                                            {row.ObjectID ? `${row.ObjectID} ` : ""}{row.Name}
                                            {row.DisplayRevision ? ` · Rev ${row.DisplayRevision}` : ""}
                                        </Typography>
                                    </div>

                                    {row.DaysOverdue !== null && row.DaysOverdue >= 0 ? (
                                        <div style={{padding: "4px 10px", borderRadius: 14, fontSize: 12,
                                                     fontWeight: 700, color: White, backgroundColor: "#D92D20",
                                                     whiteSpace: "nowrap"}}>
                                            {row.DaysOverdue}d overdue
                                        </div>
                                    ) : null}

                                    <div onClick={() => navigate("/my-training")}
                                         style={{padding: "7px 14px", borderRadius: 18, fontSize: 12,
                                                 fontWeight: 600, color: White, backgroundColor: Blue3,
                                                 cursor: "pointer", whiteSpace: "nowrap"}}>
                                        Start Now →
                                    </div>
                                </div>
                            );
                        })}

                        {/* ALWAYS THERE, not only when the list was truncated. It is the way to the page
                            that does the work, and a link that appears only sometimes is a link nobody
                            learns. */}
                        <Typography onClick={() => navigate("/my-training")}
                                    style={{fontSize: 13, fontWeight: 600, color: Blue3, cursor: "pointer",
                                            marginTop: 4, display: "inline-block"}}>
                            {pending.length > SHOWN
                                ? `View all ${pending.length} pending →`
                                : "View all pending →"}
                        </Typography>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
